/**
 * BB-11 call lifecycle: agent-facing translations for the backend's
 * normalized `HangupRefusal` values
 * (backend/app/telephony/owned_call_lifecycle/contracts.py).
 *
 * Mirrors presenceFailureCopy.js's discipline exactly -- never surfaces
 * VICIdial topology, session material, provider identifiers, raw
 * responses or exception strings. Every value is a short, fixed,
 * reviewed sentence.
 *
 * Deliberately neutral about *why* a call could not be ended. An agent
 * needs to know the call is still theirs and what to do next; the
 * distinction between a provider refusal and an unverified teardown is
 * operator diagnostics, not agent copy.
 */
export const HANGUP_REFUSAL_COPY = Object.freeze({
  no_owned_call: "There's no active call to end.",
  not_connected: "That call is already being ended.",
  teardown_context_missing: "This call can't be ended from here. Contact your administrator.",
  // D1. Added because its absence was itself the defect: BB-12.1 introduced
  // `call_control_unavailable` on the backend without copy here, so it fell
  // through to DEFAULT_HANGUP_FAILURE_MESSAGE -- which says "try again", and
  // retrying is the one thing that cannot repair this condition. The live-call
  // runtime stopped on an observation the system had already refused to treat
  // as proof of anything, so the next attempt is refused on the same grounds.
  //
  // Says so plainly rather than staying silent about it: "not instructing a
  // retry" would still leave an agent clicking a button that can only refuse.
  // Deliberately distinct wording from `teardown_context_missing` -- these are
  // different conditions, and a one-to-one refusal/copy mapping is what lets an
  // operator read the condition off a screenshot at all.
  call_control_unavailable:
    "MissionAssist can no longer manage this call. Retrying won't help — contact your administrator.",
  lease_lost: "Your dialer session ended. Log in again.",
  not_configured: "Ending calls is temporarily unavailable. Contact your administrator.",
});

export const DEFAULT_HANGUP_FAILURE_MESSAGE =
  "The call couldn't be ended. It's still connected — try again.";

export function hangupRefusalMessage(refusal) {
  if (!refusal) return DEFAULT_HANGUP_FAILURE_MESSAGE;
  return HANGUP_REFUSAL_COPY[refusal] || DEFAULT_HANGUP_FAILURE_MESSAGE;
}
